/* import __COLOCATED_TEMPLATE__ from './edit.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type Model from '@ember-data/model';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import { type TaskGenerator } from 'ember-concurrency';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import {
  KNOWLEDGE_HUB_DEFAULT_THEME_COLOR,
  DRAWER_EDITOR_SHOW_SIDE_PANEL,
  type ScheduleMode,
} from 'embercom/lib/knowledge-hub/constants';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import { type KnowledgeHubEditorConfig } from 'embercom/objects/knowledge-hub/knowledge-hub-editor-config';
import { tracked } from '@glimmer/tracking';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import storage from 'embercom/vendor/intercom/storage';
import { type AIScorePillData } from 'embercom/components/content-readiness/side-panel';
import { ReviewStatus, type EntityScoreIssue } from 'embercom/components/content-readiness/types';
import { put } from 'embercom/lib/ajax';
import {
  buildDiffDom,
  removeBlockHighlight,
  scrollToAndHighlightBlock,
  scrollToAndHighlightTitle,
  scrollToBlock,
} from 'embercom/lib/content-readiness/scroll-to-block';

interface Args {
  activeContent: LocalizedKnowledgeContent & Model;
  isContentReadinessContext?: boolean;
}

export default class Edit extends Component<Args> {
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare appService: AppService;
  @service declare permissionsService: any;

  @trackedInLocalStorage({
    keyName: DRAWER_EDITOR_SHOW_SIDE_PANEL,
  })
  showSidePanel: boolean;

  @tracked showContentReadinessPanel = true;

  private pendingAcceptedIssueIds = new Set<number>();

  constructor(owner: any, args: Args) {
    super(owner, args);

    this.showSidePanel = storage.get(DRAWER_EDITOR_SHOW_SIDE_PANEL) ?? true;

    // Front end fix to enable customers to publish articles https://github.com/intercom/intercom/issues/367694
    if (!this.args.activeContent?.writer) {
      this.args.activeContent.writer = this.appService.app.currentAdmin;
    }
  }

  get defaultThemeColor(): string {
    return KNOWLEDGE_HUB_DEFAULT_THEME_COLOR;
  }

  get editorConfig(): KnowledgeHubEditorConfig | undefined {
    return this.args.activeContent && this.args.activeContent.editorConfig
      ? this.args.activeContent.editorConfig
      : undefined;
  }

  get paywallConfig() {
    return this.knowledgeHubEditorService.paywallConfig;
  }

  @tracked aiScorePillData: AIScorePillData | null = null;

  get isContentReadinessContext(): boolean {
    return (
      !!this.args.isContentReadinessContext && this.appService.app.canUseContentReadinessScoringUI
    );
  }

  get showAiScorePill(): boolean {
    return (
      this.isContentReadinessContext && !this.showContentReadinessPanel && !!this.aiScorePillData
    );
  }

  @action
  handleAiScorePillDataChange(data: AIScorePillData | null) {
    this.aiScorePillData = data;
  }

  get detailsPanelIsOpen(): boolean {
    if (this.isContentReadinessContext) {
      return !this.showContentReadinessPanel && this.showSidePanel;
    }
    return this.showSidePanel;
  }

  private resolveBlockIndex(issue: EntityScoreIssue): number | null {
    return (
      this.knowledgeHubEditorService.composerInstance?.composer.commands.getTrackedBlockIndex(
        issue.id,
      ) ?? issue.block_index
    );
  }

  @action
  handleScoreDataLoaded(issues: EntityScoreIssue[]) {
    this.knowledgeHubEditorService.composerInstance?.composer.commands.trackIssuePositions(issues);
  }

  @action
  handleIssueClick(issue: EntityScoreIssue) {
    this.knowledgeHubEditorService.composerInstance?.composer.commands.removeBlockDiff();
    removeBlockHighlight();

    if (issue.fix_target === 'title') {
      scrollToAndHighlightTitle();
      return;
    }

    let blockIndex = this.resolveBlockIndex(issue);
    if (blockIndex === null) {
      return;
    }

    if (issue.suggested_fix_blocks?.length) {
      scrollToBlock(blockIndex);
      let diffDom = buildDiffDom(issue.suggested_fix_blocks);
      this.knowledgeHubEditorService.composerInstance?.composer.commands.showBlockDiff(
        blockIndex,
        diffDom,
      );
    } else {
      scrollToAndHighlightBlock(blockIndex);
    }
  }

  @action
  handleAcceptIssueFix(issue: EntityScoreIssue) {
    this.pendingAcceptedIssueIds.add(issue.id);

    let content = this.args.activeContent;
    if (issue.fix_target === 'title') {
      let latestVersion = (content as any).latestVersion;
      if (latestVersion) {
        latestVersion.set('versionTitle', issue.suggested_fix);
      }
    } else {
      let blockIndex = this.resolveBlockIndex(issue);
      if (blockIndex !== null && issue.suggested_fix_blocks?.length) {
        this.knowledgeHubEditorService.composerInstance?.composer.commands.replaceBlockAtIndex(
          blockIndex,
          issue.suggested_fix_blocks,
        );
      }
    }
    this.knowledgeHubEditorService.composerInstance?.composer.commands.removeBlockDiff();
    removeBlockHighlight();
  }

  @action
  toggleContentReadinessPanel() {
    this.showContentReadinessPanel = !this.showContentReadinessPanel;
  }

  @action
  toggleSidePanel(): void {
    this.showSidePanel = !this.showSidePanel;
    this.knowledgeHubEditorService.trackAnalyticsEvent(
      `${this.showSidePanel ? 'show' : 'hide'}_details_side_panel`,
    );
  }

  @action
  cancelEditActiveContent(): void {
    this.knowledgeHubEditorService.cancelEditActiveContent();
  }

  private async flushPendingReviews(): Promise<void> {
    let ids = [...this.pendingAcceptedIssueIds];
    this.pendingAcceptedIssueIds.clear();
    for (let issueId of ids) {
      try {
        await put('/teammate_app/content_readiness/review_issue', {
          app_id: this.appService.app.id,
          issue_id: issueId,
          review_status: ReviewStatus.Accepted,
        });
      } catch (e: unknown) {
        this.pendingAcceptedIssueIds.add(issueId);
      }
    }
  }

  @dropTask *saveActiveContent(): TaskGenerator<void> {
    try {
      yield this.permissionsService.checkPermission(
        this.args.activeContent.requiredEditPermissionForKnowledgeHub,
      );
    } catch (e: unknown) {
      return;
    }

    yield this.knowledgeHubEditorService.saveActiveContent();
    if (this.isContentReadinessContext) {
      yield this.flushPendingReviews();
    }
  }

  @restartableTask *autoSaveActiveContent(): TaskGenerator<void> {
    if (
      !this.permissionsService.currentAdminCan(
        this.args.activeContent.requiredEditPermissionForKnowledgeHub,
      ) ||
      !this.args.activeContent?.autoSaveEnabled ||
      !this.args.activeContent?.hasUnsavedChanges ||
      !this.args.activeContent?.validations?.isValid ||
      // prevents auto-save (flashing) when version history overlay is open - see https://github.com/intercom/intercom/issues/361362
      this.knowledgeHubEditorService.showHistoryOverlay
    ) {
      return;
    }

    yield timeout(ENV.APP._5000MS);

    if (this.knowledgeHubEditorService.isComposerUploading) {
      return;
    }

    yield this.knowledgeHubEditorService.autoSaveContent();
  }

  @dropTask
  *publishActiveContent(): TaskGenerator<void> {
    try {
      yield this.permissionsService.checkPermission(
        this.args.activeContent.requiredPublishPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }

    yield this.knowledgeHubEditorService.publishActiveContent();
    if (this.isContentReadinessContext) {
      yield this.flushPendingReviews();
    }
  }

  @dropTask
  *scheduleActiveContentUpdate(params: {
    mode: ScheduleMode;
    scheduledDate: Date;
  }): TaskGenerator<void> {
    try {
      yield this.permissionsService.checkPermission(
        this.args.activeContent.requiredPublishPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }

    yield this.knowledgeHubEditorService.scheduleActiveContentUpdate(params);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Edit': typeof Edit;
    'knowledge-hub/content-editor/edit': typeof Edit;
  }
}
